/**
 * Header.vue
 * The main header component that provides navigation and language switching functionality.
 * Features:
 * - Responsive navigation menu with mobile support
 * - Language switching between Danish and English
 * - News notification counter
 * - Logo with home link
 * - Navigation links for feedback, news, and about pages
 * - Dark theme styling
 * - Store integration for state management
 */
<template>
  <header
    id="ds-header"
    class="ds-header"
    data-theme="dark"
    style="margin-bottom:0;"
  >
    <div class="hkpn-container">
      <router-link
        to="/"
        class="quiet hkpn-logo"
        role=""
        @click="resetStore"
      >
        <ds-logo-title
          id="hkpn-hover-icon"
          title="Historiske Kort"
          byline="Klimadatastyrelsen"
          style="margin-bottom: 0;"
        />
      </router-link>
      <ds-nav-responsive>
        <button
          slot="toggle"
          title="toggle"
        >
          <svg><use href="@/assets/icons/icons.svg#burgerbar" /></svg>
        </button>
        <nav class="hkpn-nav ds-panel">
          <router-link
            to="/feedback"
          >
            <span class="medium">
              <svg><use href="@/assets/icons/icons.svg#mail" /></svg>
            </span>
            <span>{{ navbar['feedback' + langKey] }}</span>
          </router-link>
          <router-link
            to="/nyheder"
            class="hkpn-news-link"
          >
            <span
              v-if="newsCount > 0"
              class="hkpn-notification"
            >
              {{ newsCount }}
            </span>
            <span class="medium">
              <svg><use href="@/assets/icons/icons.svg#message" /></svg>
            </span>
            <span>
              {{ navbar['nyheder' + langKey] }}
            </span>
          </router-link>
          <router-link
            to="/om"
            @click="emit('reset-news-count')"
          >
            <span class="medium">
              <svg><use href="@/assets/icons/icons.svg#map" /></svg>
            </span>
            <span>
              {{ navbar['omHistoriskeKort' + langKey] }}
            </span>
          </router-link>
          <button
            role="link"
            class="hkpn-nav-button"
            @click="emit('langKey', langKey==='EN'?'DA': 'EN')"
          >
            <span class="medium">
              <svg v-if="langKey==='EN'"><use href="@/assets/icons/icons.svg#english" /></svg>
              <svg v-else><use href="@/assets/icons/icons.svg#danish" /></svg>
            </span>
            <span>{{ navbar['sprog' + langKey] }}</span>
          </button>
        </nav>
      </ds-nav-responsive>
    </div>
  </header>
</template>

<script setup>
import { useCMSStore } from '../../../store/pinia/cmsStore'
import { useRouter } from 'vue-router'
import { computed, inject } from 'vue'
import { useArkivMetaStore } from '../../../store/pinia/arkivMetaStore'

const cmsStore = useCMSStore()
const arkivmetaStore = useArkivMetaStore()

const router = useRouter()

const emit = defineEmits(['langKey', 'reset-news-count'])

// Component props
defineProps({
  newsCount: {
    type: Number,
    default: 1
  },
  showHero: {
    type: Boolean,
    default () {
      return true
    }
  }
})

// Language key injection and computed properties
const getLangKey = inject('getLangKey')
const langKey = computed(() => getLangKey())
const navbar = computed(() => cmsStore.getSingleton('navbar'))

/**
 * Resets the store state and navigates to homepage
 * Clears all filters and search parameters
 */
const resetStore = () => {
  // Navigate to homepage and ensure query parameters are cleared
  if(arkivmetaStore.getIsKort()){
    arkivmetaStore.setKortSearch()
    router.replace('/').then(() => {
      // After router navigation completes, clear search parameters
      window.location.search = ''
    })
  }
  else{
    arkivmetaStore.setKortSearch()
    router.replace('/dokumenter').then(() => {
      // After router navigation completes, clear search parameters
      window.location.search = ''
    })

  }

}
</script>

<style scoped>
.hkpn-nav {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 1rem;
  font-size: 1rem;
  font-weight: 570;
  line-height: 1.12;
  letter-spacing: .02rem;
  color: var(--white);
  background-color: inherit;
  background: inherit;
  --link: var(--c6)
}
.hkpn-nav a{
  color: var(--white) !important;
  background-color: inherit;
  outline: none !important; /* DS Override */
}
.hkpn-nav button {
  border-radius: 0;
  color: var(--white);
  background-color: inherit;
  border: none;
}
button[role="link"].hkpn-nav-button:has(use[href*="icon"]) {
  display: inline-flex;
  justify-content: left;
  border-radius: 0;
  color: var(--white);
  border: none;
  padding-left: var(--space-sm);
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  text-align: inherit;
  word-wrap: inherit;
  white-space: inherit;
}
.hkpn-nav button:hover {
  text-decoration: none;
  background: linear-gradient(0deg, var(--link), var(--link)) no-repeat left bottom/100% 9%;
  transition: color .3s ease-, background-size .3s ease-in-out, outline .3s !important;
}


svg {
  --ds-icon-color: white !important;
}
.hkpn-container {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  padding: var(--space);
  margin-left: var(--gap-lg);
  margin-right: var(--space);
  margin-bottom: 0;
  margin-top: var(--space)
}
.hkpn-news-link {
  position: relative;
}
.hkpn-notification {
  overflow-y: visible;
  z-index: 10;
  background-color: var(--link);
  display: flex;
  align-items: center;
  justify-content: center;
  content: attr(data-count);
  position: absolute;
  top: 0;
  right: -1.2rem;
  height: 1.25rem;
  min-width: 1.25rem;
  padding: 0 var(--space-xs);
  border-radius: .66rem;
  background-color: var(--primary);
  color: var(--white);
  font-size: .66rem;
  font-weight: 600;
}
header {
  width: 100vw;
  position: absolute;
  background-color: transparent;
  background: transparent;
}
ds-nav-responsive.compact .hkpn-nav {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 20rem;
  background-color: var(--background-color);
  background: inherit;
  padding: var(--space-md);
  flex-flow: column nowrap;
  align-items: flex-start;
  height: 100vh;
  z-index: 100;
}

</style>
